import { ObbAnnotation } from "../api/Application";
import { ListGroup } from "react-bootstrap";
import { memo } from "react";
import { ClassifyOutput, parseModelVehicleType } from "../DatasetImageAnnotationPageWebGpu";
import { VehicleType } from "../components/Annotation/VehicleType";

export function getVehicleTypeColor( type: VehicleType ) {
    switch( type ) {
        case VehicleType.Car:         { return "#818181"; }
        case VehicleType.Convertible: { return "#f00"; }
        case VehicleType.Freight:     { return "#6b4307"; }
        case VehicleType.Suv:         { return "#00f"; }
        case VehicleType.Pickup:      { return "#77f"; }
        case VehicleType.Unsure:      { return "#FAF"; }
        case VehicleType.Van:         { return "#000"; }
        default: { return "#a0a"; }
    }
}

export function loadImageToCanvas( imageUrl: string ): Promise<{ canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D }> {
    return new Promise( ( resolve, reject ) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement( 'canvas' );
            const ctx = canvas.getContext( '2d' )!;
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage( img, 0, 0 );
            resolve( { canvas, ctx } );
        };
        img.onerror = () => reject( new Error( 'Failed to load image' ) );
        img.src = imageUrl;
    } );
}

export function generateThumbnail( canvas: HTMLCanvasElement, annotation: ObbAnnotation, imageWidth: number, imageHeight: number ): string {
    // console.groupCollapsed( `generated thumbnail` );
    const centerX = annotation.x * imageWidth;
    const centerY = annotation.y * imageHeight;
    const baseSize = Math.max(annotation.width * imageWidth, annotation.height * imageHeight);
    const paddingFactor = 1.5;
    // OR use fixed pixel padding:
    // const paddingPixels = 20;
    // const size = baseSize + paddingPixels;
    const size = baseSize * paddingFactor;    
    const x1   = Math.max( 0, Math.min( centerX - size / 2, imageWidth ) );
    const y1   = Math.max( 0, Math.min( centerY - size / 2, imageHeight ) );
    const x2   = Math.max( 0, Math.min( centerX + size / 2, imageWidth ) );
    const y2   = Math.max( 0, Math.min( centerY + size / 2, imageHeight ) );
    const w    = Math.max( 1, ( x2 - x1 ) );
    const h    = Math.max( 1, ( y2 - y1 ) );    
    const thumbCanvas = document.createElement('canvas');
    const thumbCtx = thumbCanvas.getContext('2d')!;
    const scale = 4;
    thumbCanvas.width  = w * scale;
    thumbCanvas.height = h * scale;
    thumbCtx.imageSmoothingEnabled = false;
    thumbCtx.drawImage( canvas, x1, y1, w, h, 0, 0, w * scale, h * scale );
    return thumbCanvas.toDataURL( 'image/png' );
}

const copyImageToClipboard = async ( dataUrl: string ) => {
    const res  = await fetch( dataUrl );
    const blob = await res.blob();
    await navigator.clipboard.write( [
        new ClipboardItem( { [blob.type]: blob } )
    ] );
};

interface ItemProps {
    obj:         ObbAnnotation;
    selected:    boolean;
    thumbnail?:  string;
    setHover:    ( obj?: ObbAnnotation ) => void;
    onClick:     ( obj:  ObbAnnotation ) => void;
    showInMap?:  ( obj:  ObbAnnotation ) => void;
    prediction?: ClassifyOutput;
}

export function Item( { obj, selected, thumbnail, setHover, onClick, showInMap, prediction }: ItemProps ) {
    const active    = selected;
    const idTextCol = active ? "text-white" : "text-primary";
    return <ListGroup.Item as="div"
        action
        active={active}
        onMouseEnter={ () => setHover( obj ) }
        onMouseLeave={ () => setHover( undefined ) }        
        onClick={() => onClick( obj )}
        style={{ cursor: 'pointer', outline: "none" }}>
        <div className="d-flex align-items-center gap-2">
            { thumbnail && (
                <img src={thumbnail}
                    alt="thumbnail"
                    style={{
                        borderRadius:   "10px",
                        width:          64,
                        height:         64,
                        objectFit:      "cover",
                        imageRendering: "pixelated"
                    }} />
            )}
            <div className="small d-flex flex-row w-100">
                <div className="d-flex flex-column flex-grow-1">
                    <div className={`${idTextCol} text-decoration-underline`}>
                        #{obj.obbAnnotationId}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "min-content auto", columnGap: "1ex" }}>
                        <div className="text-secondary">
                            Color
                        </div>
                        <div className="">
                            {obj.attributes.color}
                        </div>
                        <div className="text-secondary">
                            Type
                        </div>
                        <div className="d-flex flex-row gap-2 align-items-center">
                            <div>{obj.attributes.vehicleType} </div>
                            <div style={{ textTransform: "capitalize" }}>
                                {prediction && <div style={{ backgroundColor: getVehicleTypeColor( parseModelVehicleType( prediction.label ) )}} 
                                    className="text-white rounded-3 p-1 small">{prediction.label ?? ""} { (prediction.confidence * 100).toFixed(0)}%</div>}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex gap-1 align-items-end justify-content-end">
                        <div className="d-flex align-items-center text-primary text-decoration-underline" onClick={ e => {
                            showInMap?.( obj );
                            e.stopPropagation();
                        }}>
                        <small>Show</small>
                    </div>
                </div>
            </div>
        </div>
    </ListGroup.Item>
}

export const MemoItem = memo( Item );